'use client';

/**
 * NSFW reveal overlay — sits on top of a blurred `<Image>` and lets the
 * viewer reveal the underlying image with a click. Three visual variants:
 *
 * - `hero`: centred AnimatedButton with localised "Tap to reveal" label.
 *   Used inside `<OptimizedImage variant="hero">` (cover images, market
 *   header, OG previews, lightbox preview at ≥ 200 px).
 * - `thumb`: centred glyph + the entire image area is the click target.
 *   Used for `<OptimizedImage variant="thumbnail">` because the surface
 *   is too small (32–96 px) to fit the labelled button without
 *   overflowing.
 * - `avatar`: full-circle clickable region with a small alert glyph
 *   centred on the surface. Avatars are 24–80 px so we can't fit a
 *   button label; the entire avatar is the click target instead.
 *
 * Reveal state is managed by the consumer via the shared
 * `useNsfwRevealStore` (see `@/stores/nsfw-reveal`); this component is a
 * pure visual primitive.
 *
 * Spec: 071-nsfw-moderation FR-022, FR-023, FR-026.
 */
import { c as _c } from "react/compiler-runtime";
import { useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { Alert01IconSolid } from '@/components/ui/icon/glyphs';
import { cn } from '@/lib/ui/utils';
interface NsfwOverlayProps {
  variant: 'hero' | 'thumb' | 'avatar' | 'thumb-or-hero';
  onReveal: () => void;
  /** Optional className applied to the root overlay div. */
  className?: string;
}

/**
 * Shared backdrop for both variants — sits absolutely positioned over the
 * underlying `<Image>` (which has `filter: blur(...)` applied). The blur is
 * applied to the image, NOT this overlay; this overlay merely darkens the
 * blurred image to improve text contrast and surfaces the click target.
 */
const BACKDROP_CLASSES = 'absolute inset-0 flex items-center justify-center bg-surface-scrim-default cursor-pointer';
export function NsfwOverlay(props) {
  const $ = _c(38);
  const t = useTranslations("image.nsfw");
  const reducedMotion = useReducedMotion() === true;
  let t0;
  if ($[0] !== props) {
    t0 = e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        props.onReveal();
      }
    };
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleKeyDown = t0;
  if (props.variant === "avatar") {
    const t1 = props.onReveal;
    let t2;
    if ($[2] !== t) {
      t2 = t("hidden_glyph_label");
      $[2] = t;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    const t3 = !reducedMotion && "transition-opacity duration-fast";
    let t4;
    if ($[4] !== props.className || $[5] !== t3) {
      t4 = cn("absolute inset-0 flex items-center justify-center bg-black/40 cursor-pointer", "rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary", t3, props.className);
      $[4] = props.className;
      $[5] = t3;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Icon icon={Alert01IconSolid} className="size-1/2 text-foreground-primary drop-shadow-md" />;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    let t6;
    if ($[8] !== handleKeyDown || $[9] !== props.onReveal || $[10] !== t2 || $[11] !== t4) {
      t6 = <div role="button" tabIndex={0} onClick={t1} onKeyDown={handleKeyDown} aria-label={t2} className={t4}>{t5}</div>;
      $[8] = handleKeyDown;
      $[9] = props.onReveal;
      $[10] = t2;
      $[11] = t4;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  if (props.variant === "thumb") {
    const t1 = props.onReveal;
    let t2;
    if ($[13] !== t) {
      t2 = t("hidden_glyph_label");
      $[13] = t;
      $[14] = t2;
    } else {
      t2 = $[14];
    }
    const t3 = !reducedMotion && "transition-opacity duration-fast";
    let t4;
    if ($[15] !== props.className || $[16] !== t3) {
      t4 = cn(BACKDROP_CLASSES, "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary", t3, props.className);
      $[15] = props.className;
      $[16] = t3;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    let t5;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Icon icon={Alert01IconSolid} className="size-1/3 text-foreground-primary" />;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    let t6;
    if ($[19] !== handleKeyDown || $[20] !== props.onReveal || $[21] !== t2 || $[22] !== t4) {
      t6 = <div role="button" tabIndex={0} onClick={t1} onKeyDown={handleKeyDown} aria-label={t2} className={t4}>{t5}</div>;
      $[19] = handleKeyDown;
      $[20] = props.onReveal;
      $[21] = t2;
      $[22] = t4;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    return t6;
  }
  const t1 = !reducedMotion && "transition-opacity duration-fast";
  let t2;
  if ($[24] !== props.className || $[25] !== t1) {
    t2 = cn(BACKDROP_CLASSES, t1, props.className);
    $[24] = props.className;
    $[25] = t1;
    $[26] = t2;
  } else {
    t2 = $[26];
  }
  const t3 = props.onReveal;
  let t4;
  if ($[27] !== props) {
    t4 = e_0 => {
      e_0.stopPropagation();
      props.onReveal();
    };
    $[27] = props;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  let t5;
  if ($[29] !== t) {
    t5 = t("tap_to_reveal");
    $[29] = t;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  let t6;
  if ($[31] !== t4 || $[32] !== t5) {
    t6 = <AnimatedButton type="button" size="sm" variant="primary" onClick={t4} prefix={Alert01IconSolid}>{t5}</AnimatedButton>;
    $[31] = t4;
    $[32] = t5;
    $[33] = t6;
  } else {
    t6 = $[33];
  }
  let t7;
  if ($[34] !== props.onReveal || $[35] !== t2 || $[36] !== t6) {
    t7 = <div className={t2} onClick={t3} role="presentation">{t6}</div>;
    $[34] = props.onReveal;
    $[35] = t2;
    $[36] = t6;
    $[37] = t7;
  } else {
    t7 = $[37];
  }
  return t7;
}